import { Autocomplete, Box, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { ResponsiveLine } from '@nivo/line'
import { FC, useEffect, useState } from 'react'
import { Title, useGetList, useTranslate } from 'react-admin'
import { CostOverviewResponse, OrganizationOut } from '../types/apiRequests'
import { border } from '../layout/theme/border'
import { xxllncColor } from '../layout/theme/color'
import { formatCompact, formatCurrency } from '../utils/formatNumber'
import { KpiCard } from './cost-explorer/KpiCard'
import { CostTable } from './cost-explorer/CostTable'
import { getJson, getUrl } from '../providers/data'
import { fadeSlideIn } from '../components/dashboard'
import { SHOW_PROXIES } from '../utils/featureFlags'

const PERIODS = ['current_month', '3m', '6m', '12m', 'all'] as const
const PERIOD_KEYS = new Map([
  ['current_month', 'periodCurrentMonth'],
  ['3m', 'period3m'],
  ['6m', 'period6m'],
  ['12m', 'period12m'],
  ['all', 'periodAll'],
])

const CostExplorer: FC = () => {
  const translate = useTranslate()
  const [period, setPeriod] = useState<string>('current_month')
  const [orgId, setOrgId] = useState<string | null>(null)
  const [data, setData] = useState<CostOverviewResponse | null>(null)
  const [loading, setLoading] = useState(true)

  const { data: orgList } = useGetList<OrganizationOut>('organization', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'display_name', order: 'ASC' },
  })

  useEffect(() => {
    setLoading(true)
    const params = new URLSearchParams({ period })
    if (orgId) params.set('organization_id', orgId)
    getJson<CostOverviewResponse>(`${getUrl()}/usage_stats/cost-overview?${params.toString()}`)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false))
  }, [period, orgId])

  if (loading && !data) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <Typography sx={{ color: 'text.secondary' }}>{translate('ra.page.loading')}</Typography>
      </Box>
    )
  }

  const kpi = data?.kpi

  const trendLabel = (trend: number | null | undefined) =>
    trend != null ? `${trend > 0 ? '+' : ''}${trend}% ${translate('costExplorer.vsLastPeriod')}` : undefined

  const chartData = [
    {
      id: translate('costExplorer.seatCosts'),
      color: xxllncColor.green100,
      data: (data?.timeline ?? []).map((p) => ({ x: p.year_month, y: p.seat_costs })),
    },
    {
      id: translate('costExplorer.mcpSlotCosts'),
      color: xxllncColor.dark100,
      data: (data?.timeline ?? []).map((p) => ({ x: p.year_month, y: p.mcp_slot_costs })),
    },
    {
      id: translate('costExplorer.workflowSlotCosts'),
      color: xxllncColor.purple100,
      data: (data?.timeline ?? []).map((p) => ({ x: p.year_month, y: p.workflow_slot_costs ?? 0 })),
    },
    {
      id: translate('costExplorer.workflowExecutionCosts'),
      color: xxllncColor.blue100,
      data: (data?.timeline ?? []).map((p) => ({ x: p.year_month, y: p.workflow_execution_costs ?? 0 })),
    },
    {
      id: translate('costExplorer.platformCosts'),
      color: xxllncColor.dark100,
      data: (data?.timeline ?? []).map((p) => ({ x: p.year_month, y: p.platform_costs })),
    },
    {
      id: translate('costExplorer.chatCosts'),
      color: xxllncColor.orange100,
      data: (data?.timeline ?? []).map((p) => ({ x: p.year_month, y: p.chat_costs })),
    },
  ]

  return (
    <Box sx={{ p: 3, maxWidth: 1200 }}>
      <Title title={translate('costExplorer.title')} />
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography sx={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em' }}>
          {translate('costExplorer.title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Autocomplete
            options={orgList ?? []}
            getOptionLabel={(o) => o.display_name}
            value={orgList?.find((o) => o.id === orgId) ?? null}
            onChange={(_ev, v) => setOrgId(v?.id ?? null)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={translate('costExplorer.organization')}
                size="small"
                sx={{ minWidth: 250 }}
              />
            )}
          />
          <ToggleButtonGroup value={period} exclusive onChange={(_ev, v: string | null) => { if (v) setPeriod(v) }} size="small">
            {PERIODS.map((p) => (
              <ToggleButton key={p} value={p} sx={{ px: 2, fontSize: 12, fontWeight: 600 }}>
                {translate(`costExplorer.${PERIOD_KEYS.get(p) ?? p}`)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <KpiCard
          label={translate('costExplorer.totalCosts')}
          value={formatCurrency(kpi?.total ?? 0)}
          accent={xxllncColor.dark100}
          subtitle={trendLabel(kpi?.trend_total)}
          delay={0}
        />
        <KpiCard
          label={translate('costExplorer.seatCosts')}
          value={formatCurrency(kpi?.seat_costs ?? 0)}
          accent={xxllncColor.green100}
          subtitle={trendLabel(kpi?.trend_seat)}
          delay={60}
        />
        <KpiCard
          label={translate('costExplorer.mcpSlotCosts')}
          value={formatCurrency(kpi?.mcp_slot_costs ?? 0)}
          accent={xxllncColor.dark100}
          subtitle={trendLabel(kpi?.trend_mcp_slot)}
          delay={120}
        />
        <KpiCard
          label={translate('costExplorer.workflowSlotCosts')}
          value={formatCurrency(kpi?.workflow_slot_costs ?? 0)}
          accent={xxllncColor.purple100}
          subtitle={trendLabel(kpi?.trend_workflow_slot)}
          delay={180}
        />
        <KpiCard
          label={translate('costExplorer.platformCosts')}
          value={formatCurrency(kpi?.platform_costs ?? 0)}
          accent="rgba(49,103,155,1)"
          subtitle={trendLabel(kpi?.trend_platform)}
          delay={240}
        />
        <KpiCard
          label={translate('costExplorer.workflowExecutionCosts')}
          value={formatCurrency(kpi?.workflow_execution_costs ?? 0)}
          accent={xxllncColor.blue100}
          subtitle={trendLabel(kpi?.trend_workflow_execution)}
          delay={300}
        />
        <KpiCard
          label={translate('costExplorer.chatCosts')}
          value={formatCurrency(kpi?.chat_costs ?? 0)}
          accent={xxllncColor.orange100}
          subtitle={trendLabel(kpi?.trend_chat)}
          delay={360}
        />
      </Box>

      {(data?.timeline?.length ?? 0) > 1 && (
        <Box
          sx={{
            background: '#fff',
            borderRadius: border.card.radius,
            border: '1px solid rgba(0,0,0,0.06)',
            p: 3,
            mb: 3,
            height: 320,
            animation: `${fadeSlideIn} 0.4s ease-out 300ms both`,
          }}
        >
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 600,
              color: 'rgba(0,0,0,0.3)',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              mb: 2,
            }}
          >
            {translate('costExplorer.costTimeline')}
          </Typography>
          <Box sx={{ height: 260 }}>
            <ResponsiveLine
              data={chartData}
              margin={{ top: 10, right: 20, bottom: 30, left: 60 }}
              xScale={{ type: 'point' }}
              yScale={{ type: 'linear', stacked: true }}
              curve="monotoneX"
              enableArea
              areaOpacity={0.15}
              lineWidth={2}
              enablePoints={false}
              enableGridX={false}
              gridYValues={5}
              axisLeft={{
                tickSize: 0,
                tickPadding: 8,
                format: (v: number) => formatCurrency(v),
              }}
              axisBottom={{
                tickSize: 0,
                tickPadding: 8,
                tickRotation: -45,
              }}
              colors={[
                xxllncColor.green100,
                xxllncColor.dark100,
                xxllncColor.purple100,
                xxllncColor.blue100,
                'rgba(49,103,155,1)',
                xxllncColor.orange100,
              ]}
              theme={{
                grid: { line: { stroke: 'rgba(0,0,0,0.04)' } },
                axis: {
                  ticks: { text: { fill: 'rgba(0,0,0,0.3)', fontSize: 10, fontWeight: 500 } },
                },
              }}
              enableSlices="x"
              sliceTooltip={({ slice }) => (
                <Box
                  sx={{
                    background: '#1a1a1a',
                    color: '#fff',
                    borderRadius: '8px',
                    p: 1.5,
                    fontSize: 12,
                  }}
                >
                  <Typography sx={{ fontSize: 11, fontWeight: 700, mb: 0.5 }}>
                    {slice.points[0]?.data.x as string}
                  </Typography>
                  {slice.points.map((p) => (
                    <Box key={p.serieId} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                      <span>{p.serieId}</span>
                      <strong>{formatCurrency(p.data.y as number)}</strong>
                    </Box>
                  ))}
                </Box>
              )}
            />
          </Box>
        </Box>
      )}

      {!orgId && (data?.by_organization?.length ?? 0) > 0 && (
        <CostTable
          title={translate('costExplorer.organizationBreakdown')}
          columns={[
            { label: translate('costExplorer.organization'), key: 'organization_name', width: 140 },
            { label: translate('costExplorer.seats'), key: 'seats', width: 70 },
            { label: translate('costExplorer.mcpSlots'), key: 'mcp_slots', width: 70 },
            { label: translate('costExplorer.workflowSlots'), key: 'workflow_slots', width: 90 },
            {
              label: translate('costExplorer.seatCosts'),
              key: 'seat_costs',
              format: formatCurrency,
              color: xxllncColor.green100,
            },
            { label: translate('costExplorer.mcpSlotCosts'), key: 'mcp_slot_costs', format: formatCurrency },
            {
              label: translate('costExplorer.workflowSlotCosts'),
              key: 'workflow_slot_costs',
              format: formatCurrency,
              color: xxllncColor.purple100,
            },
            {
              label: translate('costExplorer.workflowExecutionCosts'),
              key: 'workflow_execution_costs',
              format: formatCurrency,
              color: xxllncColor.blue100,
            },
            {
              label: translate('costExplorer.platformCosts'),
              key: 'platform_costs',
              format: formatCurrency,
              color: xxllncColor.dark100,
            },
            {
              label: translate('costExplorer.chatCosts'),
              key: 'chat_costs',
              format: formatCurrency,
              color: xxllncColor.orange100,
            },
            { label: translate('costExplorer.total'), key: 'total', format: formatCurrency, bold: true },
          ]}
          rows={(data?.by_organization ?? []).map((o) => ({ ...o }))}
          delay={400}
        />
      )}

      {orgId && (
        <>
          {(data?.by_server?.length ?? 0) > 0 && (
            <CostTable
              title={translate('costExplorer.serverBreakdown')}
              columns={[
                { label: 'Server', key: 'name' },
                { label: translate('costExplorer.calls'), key: 'calls', format: formatCompact },
                {
                  label: translate('costExplorer.cost'),
                  key: 'cost',
                  format: formatCurrency,
                  bold: true,
                },
              ]}
              rows={(data?.by_server ?? []).map((s) => ({ ...s }))}
              delay={400}
            />
          )}
          {(data?.by_workflow?.length ?? 0) > 0 && (
            <CostTable
              title={translate('costExplorer.workflowBreakdown')}
              columns={[
                { label: translate('costExplorer.workflow'), key: 'name' },
                {
                  label: translate('costExplorer.executions'),
                  key: 'executions',
                  format: formatCompact,
                },
                {
                  label: translate('costExplorer.executionCost'),
                  key: 'execution_cost',
                  format: formatCurrency,
                },
                {
                  label: translate('costExplorer.aiTokens'),
                  key: 'ai_tokens',
                  format: formatCompact,
                },
                {
                  label: translate('costExplorer.aiCostEstimated'),
                  key: 'ai_cost',
                  format: formatCurrency,
                },
                {
                  label: translate('costExplorer.cost'),
                  key: 'cost',
                  format: formatCurrency,
                  bold: true,
                },
              ]}
              rows={(data?.by_workflow ?? []).map((w) => ({ ...w }))}
              delay={500}
            />
          )}
          {SHOW_PROXIES && (data?.by_proxy?.length ?? 0) > 0 && (
            <CostTable
              title={translate('costExplorer.proxyBreakdown')}
              columns={[
                { label: translate('costExplorer.proxy'), key: 'name' },
                { label: translate('costExplorer.calls'), key: 'calls', format: formatCompact },
                {
                  label: translate('costExplorer.cost'),
                  key: 'cost',
                  format: formatCurrency,
                  bold: true,
                },
              ]}
              rows={(data?.by_proxy ?? []).map((s) => ({ ...s }))}
              delay={600}
            />
          )}
        </>
      )}
    </Box>
  )
}

export default CostExplorer
